import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';
import { FavouriteButton } from '@noths/polaris-design-system-candidate-components';
import Image from 'next/image';

import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { PreventSwipeNavigation } from 'src/components/molecules/PreventSwipeNavigation/PreventSwipeNavigation';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import { ThumbnailsContainer } from 'src/components/molecules/Thumbnails/ThumbnailsContainer';
import type {
  MediaItem,
  NextOrPreviousPayload,
  OpenModalPayload,
  SetCurrentMediaPayload,
} from 'src/components/organisms/ProductMediaCarousel/types';
import { selectCurrentMedia, selectNavigatedBy } from 'src/redux/productMediaCarousel/selectors';
import { PRODUCT_MEDIA_SECTION_LABEL_SINGLE } from './constants/copy';
import {
  PRODUCT_MEDIA_TABPANEL_CLASS,
  PRODUCT_MEDIA_TABPANEL_CONTAINER_ID,
  PRODUCT_MEDIA_TABPANEL_ID_PREFIX,
} from './constants/elementIds';
import { mediaKey } from './media/buildProductMedia';
import { useProductMediaCarousel } from './useProductMediaCarousel';
import * as styles from './ProductMediaCarousel.styles';

export interface ProductMediaCarouselStateProps {
  favouriteProduct: boolean;
  media: MediaItem[];
  productCode: number;
}

export interface ProductMediaCarouselDispatchProps {
  onFavouriteClick: (productCode: number) => void;
  onNextMedia: (payload: NextOrPreviousPayload) => void;
  onOpenModal: (payload: OpenModalPayload) => void;
  onPrevMedia: (payload: NextOrPreviousPayload) => void;
  setCurrentMedia: (payload: SetCurrentMediaPayload) => void;
}

export type ProductMediaCarouselProps = ProductMediaCarouselStateProps &
  ProductMediaCarouselDispatchProps;

export const ProductMediaCarousel = ({
  favouriteProduct,
  media,
  onFavouriteClick,
  onNextMedia,
  onOpenModal,
  onPrevMedia,
  productCode,
  setCurrentMedia,
}: ProductMediaCarouselProps) => {
  const currentMedia = useSelector(selectCurrentMedia);
  const navigatedBy = useSelector(selectNavigatedBy);
  const totalMedia = media.length;
  const location = 'Main media';
  const [mediaFocus, setMediaFocus] = React.useState(false);
  const [hasInteractedWithCarousel, setHasInteractedWithCarousel] = React.useState(false);
  const [loadedMedia, setMediaLoaded] = React.useState<Record<number, boolean>>({});

  useEffect(() => {
    if (currentMedia > 0) {
      // User has navigated past the first media item
      setHasInteractedWithCarousel(true);
    }
  }, [currentMedia]);

  const [carouselContainerRef, carouselItemRefs] = useProductMediaCarousel(
    (entry) => {
      const mediaIndex = Number((entry.target as HTMLElement).dataset.itemIndex);
      if (currentMedia != mediaIndex) {
        setCurrentMedia({ mediaIndex, navigationType: 'swipe', location });
      }
    },
    currentMedia,
    navigatedBy,
    totalMedia,
  );

  return (
    <>
      <PreventSwipeNavigation aria-live="polite" css={[styles.mediaWrapper]}>
        <section aria-label={PRODUCT_MEDIA_SECTION_LABEL_SINGLE} css={[styles.carouselContainer]}>
          <div
            css={[styles.mainMediaWrapperWithThumbnails, styles.allRoundedCorners]}
            data-media-focus={mediaFocus}
            id={PRODUCT_MEDIA_TABPANEL_CONTAINER_ID}
            ref={carouselContainerRef}
          >
            {media.map((item, i) => {
              if (item.type === 'video') {
                return (
                  <div
                    className={PRODUCT_MEDIA_TABPANEL_CLASS}
                    css={styles.carouselSlide}
                    data-item-index={i}
                    data-media-focus={mediaFocus}
                    id={`${PRODUCT_MEDIA_TABPANEL_ID_PREFIX}${i}`}
                    key={mediaKey(item)}
                    ref={(node) => {
                      carouselItemRefs.current[i] = node as HTMLElement;
                    }}
                  >
                    <ProductVideoSlide
                      contentPosition={i + 1}
                      isActive={currentMedia === i}
                      location="Main media"
                      onExpand={() => onOpenModal({ location: 'Main media' })}
                      playbackId={item.playbackId}
                      posterUrl={item.posterUrl}
                      title={item.title}
                      totalOptions={totalMedia}
                    />
                  </div>
                );
              }

              const { href: src, title } = item;

              return (
                <button
                  aria-label={`${title}, ${i + 1} of ${totalMedia}`}
                  className={PRODUCT_MEDIA_TABPANEL_CLASS}
                  css={styles.carouselSlide}
                  data-item-index={i}
                  data-media-focus={mediaFocus}
                  id={`${PRODUCT_MEDIA_TABPANEL_ID_PREFIX}${i}`}
                  key={mediaKey(item)}
                  onBlur={() => setMediaFocus(false)}
                  onClick={() => onOpenModal({ location: 'Main media' })}
                  onFocus={(e) => setMediaFocus(isKeyboardTriggeredEvent(e))}
                  ref={(node) => {
                    carouselItemRefs.current[i] = node as HTMLElement;
                  }}
                  tabIndex={currentMedia === i ? undefined : -1}
                  type="button"
                >
                  <Image
                    alt={`${title}, ${i + 1} of ${totalMedia}`}
                    css={styles.mainMedia}
                    fill={true}
                    onLoad={() => setMediaLoaded({ ...loadedMedia, [i]: true })}
                    priority={i === 0}
                    src={getProductImageURL(
                      src,
                      i === 0 || hasInteractedWithCarousel ? 'original' : 'normal',
                    )}
                    unoptimized
                  />
                </button>
              );
            })}
          </div>
          {totalMedia > 1 && (
            <>
              <PreviousButton onClick={() => onPrevMedia({ location, totalMedia })} />
              <NextButton onClick={() => onNextMedia({ location, totalMedia })} />
              <ImageNumberLabel currentImage={currentMedia + 1} totalImages={totalMedia} />
            </>
          )}
          <FavouriteButton
            active={favouriteProduct}
            css={styles.favouriteButton}
            onClick={() => onFavouriteClick(productCode)}
            size="lg"
          />
        </section>
      </PreventSwipeNavigation>
      {totalMedia > 1 && (
        <div css={styles.mediaThumbnails}>
          <ThumbnailsContainer location={location} />
        </div>
      )}
    </>
  );
};
